<template>
  <div
    class="bui-tbl-row-cell"
    :class="[
      `kind-${kind}`,
      {
        first: isFirst,
        last: isLast,
        selected,
        hovered,
        preview: hasPreviewHeight,
        'last-row': isLastRow,
      },
    ]"
    role="cell"
    @click="onClick"
    @mouseenter="onMouseEnter"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    kind?:
      | 'name'
      | 'namePreview'
      | 'preview'
      | 'text'
      | 'tags'
      | 'status'
      | 'usedBy'
      | 'actions'
      | 'actionsList'
      | 'checkbox';
    isFirst?: boolean;
    isLast?: boolean;
    /** Last data row in the table (no bottom divider after this row). */
    isLastRow?: boolean;
    selected?: boolean;
    hovered?: boolean;
    /** Force the 104px preview row height even on non-preview cells of a preview row. */
    preview?: boolean;
  }>(),
  {
    kind: 'text',
    isFirst: false,
    isLast: false,
    isLastRow: false,
    selected: false,
    hovered: false,
    preview: false,
  },
);

const emit = defineEmits<{ click: [event: MouseEvent] }>();

const hasPreviewHeight = computed(
  () => props.preview || props.kind === 'preview' || props.kind === 'namePreview',
);

function onClick(e: MouseEvent) {
  emit('click', e);
}

/** Cell kinds whose content is clamped to the column and ellipsized. */
const TRUNCATING_KINDS = new Set(['text', 'name', 'namePreview']);

/**
 * Truncated content must stay reachable: expose the cell's full text as a
 * native title tooltip. Set lazily on hover (render fns produce arbitrary
 * DOM, so the text isn't known up front); inner elements that carry their
 * own `title` still win, since the browser uses the nearest titled ancestor.
 */
function onMouseEnter(e: MouseEvent) {
  if (!TRUNCATING_KINDS.has(props.kind)) return;
  const el = e.currentTarget as HTMLElement;
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) el.title = text;
}
</script>

<style lang="scss" scoped>
.bui-tbl-row-cell {
  display: flex;
  align-items: center;
  gap: var(--bui-tbl-cell-gap, 22px);
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  min-height: var(--bui-tbl-row-min-h, 68px);
  max-height: var(--bui-tbl-row-max-h, 104px);
  padding: 0 var(--bui-tbl-cell-pad-x, 16px);
  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  position: relative;
  transition: background-color 120ms ease;
  border-bottom: 1px solid var(--bui-tbl-line, #ffd9e6);

  &.last-row {
    border-bottom: none;
  }

  &.preview {
    min-height: var(--bui-tbl-row-min-h-preview, 104px);
    padding-top: var(--bui-tbl-preview-pad-y, 4px);
    padding-bottom: var(--bui-tbl-preview-pad-y, 4px);
  }

  @media (max-width: 768px) {
    &.preview {
      --bui-tbl-preview-pad-y: 2px;
    }
  }

  &.kind-actions,
  &.kind-actionsList {
    justify-content: center;
    padding: 0 8px;
    gap: 1px;
  }

  &.kind-usedBy {
    justify-content: center;
    padding: 0 8px;
  }

  &.kind-status,
  &.kind-tags {
    padding: 0 var(--bui-tbl-cell-pad-x, 16px);
  }

  /* Text-ish cells clamp their content to the column and ellipsize; the
     full value stays reachable via the native title tooltip (see
     onMouseEnter). Control cells (checkbox/status/actions/usedBy/tags)
     are deliberately untouched. */
  &.kind-text,
  &.kind-name,
  &.kind-namePreview {
    min-width: 0;

    > :deep(*) {
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Structured name cells: ellipsize the link line and the sub line
       independently (a block child would otherwise escape the parent's
       single-line ellipsis). */
    :deep(.bui-cell-name) {
      > a,
      > .title-link {
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: bottom;
      }

      .bui-cell-sub {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    /* Multi-line meta lists (e.g. player tags): ellipsize each line. */
    :deep(.bui-meta-list) > div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.first {
    filter: drop-shadow(var(--bui-tbl-row-shadow-side, 1px 0 4px rgba(179, 185, 242, 0.16)));
  }

  &.selected,
  &.hovered {
    background: var(--bui-tbl-rowbtn-hover-fill, #ececf0);
  }

  // Body text style — 14px / Robout Regular
  font-family: inherit;
  font-size: var(--bui-tbl-row-font-size, 14px);
  line-height: var(--bui-tbl-row-line-height, 17px);

  ::v-slotted(a),
  ::v-slotted(.title-link),
  :deep(a.title-link),
  :deep(.title-link) {
    color: var(--bui-tbl-row-link, #4b1886);
    text-decoration: none;
    font-weight: 500;
  }

  ::v-slotted(a:visited),
  ::v-slotted(.title-link:visited),
  :deep(a.title-link:visited),
  :deep(.title-link:visited) {
    color: var(--bui-tbl-row-link-visited, var(--bui-tbl-row-link, #4b1886));
  }

  ::v-slotted(a:hover),
  ::v-slotted(.title-link:hover),
  :deep(a.title-link:hover),
  :deep(.title-link:hover) {
    color: var(--bui-tbl-row-link-hover, var(--bui-tbl-row-link, #4b1886));
    text-decoration: underline;
  }
}
</style>
